import { useState, useEffect, useRef } from "react";
import dayjs from "dayjs";
import { useSWRConfig } from "swr";

import {
  useTheme,
  Grid,
  Typography,
  Button,
  IconButton,
  Chip,
  Box,
  TextField,
  Tooltip,
  CircularProgress,
  ThemeProvider
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

import { Autocomplete } from "@aclymatepackages/atoms";
import { MultiPartFormLayout } from "@aclymatepackages/multi-part-form";
import mainTheme, { mergeDarkTheme } from "@aclymatepackages/themes";
import { calculateMergedProductFootprint } from "@aclymatepackages/products";

import Backdrop from "../../atoms/mui/Backdrop";
import ReportRenderer from "../../platform-pages/dashboard/views/customers/ReportRenderer";
import ProductPdfCapture from "../../platform-pages/dashboard/views/customers/ProductPdfCapture";

import {
  useAccountData,
  useCachedDisplayData,
  useCachedFirebaseCrud
} from "../../../helpers/firebase";
import {
  fetchCompanyDataFromName,
  fetchOurApi
} from "../../../helpers/utils/apiCalls";
import { getAccountId } from "../../../helpers/accountHelpers";
import useEmissionsContext from "../../../helpers/contexts/emissions";
import useProductsEmissionsFactors from "../../../helpers/contexts/ProductsEmissionsFactors";

const CompanySearchInput = ({ onSearchComplete }) => {
  const [description, setDescription] = useState("");
  const [name, setName] = useState("");
  const [searching, setSearching] = useState(false);

  const handleSearch = () => {
    setSearching(true);
    onSearchComplete({ name, description });
  };

  return (
    <Grid container direction="column" spacing={2}>
      <Grid item>
        <TextField
          label="What does your customer do?"
          size="small"
          fullWidth
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Grid>
      <Grid item>
        <TextField
          label="What is your customer's company name?"
          size="small"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          helperText="Use the full company name (e.g. United Airlines instead of United)."
        />
      </Grid>
      <Grid item container justifyContent="center">
        <Grid item>
          <Button
            variant="contained"
            disabled={!name || !description || searching}
            onClick={handleSearch}
          >
            Search
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};

const emptyContact = () => ({ id: crypto.randomUUID(), name: "", email: "" });

const buildAvailableYears = (startDate) => {
  const startYear = dayjs(startDate).year();
  const currentYear = dayjs().year();
  return Array.from(
    { length: currentYear - startYear },
    (_, i) => startYear + i
  );
};

const ContactsInput = ({ contacts, setContacts }) => {
  const addContact = () => setContacts((prev) => [...prev, emptyContact()]);

  const removeContact = (id) =>
    setContacts((prev) => prev.filter((c) => c.id !== id));

  const updateContact = (id, field, value) =>
    setContacts((prev) =>
      prev.map((c) => (c.id === id ? { ...c, [field]: value } : c))
    );

  return (
    <Grid container direction="column" spacing={2}>
      {contacts.map((contact) => (
        <Grid item key={contact.id}>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={5}>
              <TextField
                label="Name"
                size="small"
                fullWidth
                value={contact.name}
                onChange={(e) =>
                  updateContact(contact.id, "name", e.target.value)
                }
              />
            </Grid>
            <Grid item xs={5}>
              <TextField
                label="Email"
                size="small"
                fullWidth
                value={contact.email}
                onChange={(e) =>
                  updateContact(contact.id, "email", e.target.value)
                }
              />
            </Grid>
            <Grid item xs={2}>
              {contacts.length > 1 && (
                <IconButton
                  size="small"
                  onClick={() => removeContact(contact.id)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Grid>
          </Grid>
        </Grid>
      ))}
      <Grid item container justifyContent="center">
        <Grid item>
          <Button
            startIcon={<AddIcon />}
            size="small"
            color="primary"
            onClick={addContact}
          >
            Add another contact
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};

const YearSelectionInput = ({
  selectedYears,
  setSelectedYears,
  accountingYears,
  startDate
}) => {
  const availableYears = startDate ? buildAvailableYears(startDate) : [];
  const lockedYears = (accountingYears || []).map(({ date }) =>
    dayjs(date).year()
  );

  const toggleYear = (year) =>
    setSelectedYears((prev) =>
      prev.includes(year) ? prev.filter((y) => y !== year) : [...prev, year]
    );

  return (
    <Grid container direction="column" spacing={1}>
      <Grid item>
        <Typography variant="body2" color="textSecondary">
          Select which completed years to share with this customer. The current
          year becomes available for sharing once it ends. You can always add
          more from the customer detail view.
        </Typography>
      </Grid>
      <Grid item container spacing={1} justifyContent="center">
        <ThemeProvider theme={mainTheme}>
          {availableYears.map((year) => {
            const isFinalized = lockedYears.includes(year);
            const isSelected = selectedYears.includes(year);
            return (
              <Grid item key={year}>
                <Tooltip title={isFinalized ? "Finalized" : "Preliminary"}>
                  <Chip
                    label={year}
                    color={isFinalized ? "secondary" : "warning"}
                    variant={isSelected ? "filled" : "outlined"}
                    onClick={() => toggleYear(year)}
                  />
                </Tooltip>
              </Grid>
            );
          })}
        </ThemeProvider>
        {availableYears.length === 0 && (
          <Grid item>
            <Typography variant="caption" color="textSecondary">
              No report years available yet.
            </Typography>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

const ProductSelectionInput = ({
  selectedProductIds,
  setSelectedProductIds,
  dbProducts,
  productLifecycleEvents
}) => {
  const options = (dbProducts || []).map(({ id, name }) => ({ id, name }));

  const selectedOptions = options.filter((o) =>
    selectedProductIds.includes(o.id)
  );

  const isFinalized = (productId) => {
    const event = (productLifecycleEvents || []).find(
      (e) => e.productId === productId
    );
    return event?.bomStatus === "confirmed";
  };

  const renderOption = (props, option) => (
    <li {...props} key={option.id}>
      <Box display="flex" alignItems="center" gap={1}>
        {option.name}
        <ThemeProvider theme={mainTheme}>
          <Chip
            label={isFinalized(option.id) ? "Finalized" : "Preliminary"}
            color={isFinalized(option.id) ? "secondary" : "warning"}
            size="small"
          />
        </ThemeProvider>
      </Box>
    </li>
  );

  return (
    <Grid container direction="column" spacing={1}>
      <Grid item>
        <Typography variant="body2" color="textSecondary">
          Select which products to share with this customer.
        </Typography>
      </Grid>
      <Grid item>
        <Autocomplete
          label="Select products"
          availableOptions={options}
          multiple
          value={selectedOptions}
          setValue={(newOptions) =>
            setSelectedProductIds((newOptions || []).map((o) => o.id))
          }
          onDelete={(removed) =>
            setSelectedProductIds((prev) =>
              prev.filter((id) => id !== removed.id)
            )
          }
          renderOption={renderOption}
          disableCloseOnSelect
          size="small"
        />
      </Grid>
    </Grid>
  );
};

const NewCustomerForm = ({ onClose }) => {
  const { palette } = useTheme();
  const { mutate } = useSWRConfig();
  const { updateCollectionDoc } = useCachedFirebaseCrud();
  const [{ name: vendorName, id: vendorId, startDate }] = useAccountData();
  const [accountingYears] = useCachedDisplayData("accounting");
  const [dbProducts] = useCachedDisplayData("product-components");
  const [productLifecycleEvents] = useCachedDisplayData(
    "product-lifecycle-events"
  );
  const { allEmissions } = useEmissionsContext();
  const [{ emissionsFactors }] = useProductsEmissionsFactors();
  const [matchedCompany, setMatchedCompany] = useState(null);
  const [contacts, setContacts] = useState([emptyContact()]);
  const [selectedYears, setSelectedYears] = useState([]);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [generatingYears, setGeneratingYears] = useState([]);
  const [generatingProductIds, setGeneratingProductIds] = useState([]);
  const [completedCount, setCompletedCount] = useState(0);
  const [totalReportCount, setTotalReportCount] = useState(0);
  const pendingSharedReportsRef = useRef([]);
  const pendingSharedProductsRef = useRef([]);
  const pendingRelationshipIdRef = useRef(null);

  useEffect(() => {
    if (totalReportCount === 0 || completedCount < totalReportCount) return;
    onClose();
  }, [completedCount, totalReportCount, onClose]);

  const onReportUploaded = async (year, url) => {
    const now = new Date();
    pendingSharedReportsRef.current = pendingSharedReportsRef.current.map(
      (r) => (r.year === year ? { ...r, generatedAt: now, reportUrl: url } : r)
    );
    const isFinalized = lockedYears.includes(year);
    const accountingDoc = (accountingYears || []).find(
      ({ date }) => dayjs(date).year() === year
    );
    const canonicalWrites =
      isFinalized && accountingDoc?.id
        ? [
            updateCollectionDoc("accounting", accountingDoc.id, {
              reportUrl: url,
              reportGeneratedAt: now
            })
          ]
        : [];
    if (!isFinalized) {
      sessionStorage.setItem(`preliminary-report-url-${vendorId}-${year}`, url);
    }
    await Promise.all([
      updateCollectionDoc("customers", pendingRelationshipIdRef.current, {
        sharedReports: pendingSharedReportsRef.current,
        sharedProducts: pendingSharedProductsRef.current
      }),
      ...canonicalWrites
    ]);
    setCompletedCount((prev) => prev + 1);
  };

  const onReportError = () => setCompletedCount((prev) => prev + 1);

  const onProductReportUploaded = async (productId, url) => {
    pendingSharedProductsRef.current = pendingSharedProductsRef.current.map(
      (p) =>
        p.productId === productId
          ? { ...p, reportUrl: url, reportShared: true }
          : p
    );
    await updateCollectionDoc("customers", pendingRelationshipIdRef.current, {
      sharedReports: pendingSharedReportsRef.current,
      sharedProducts: pendingSharedProductsRef.current
    });
    setCompletedCount((prev) => prev + 1);
  };

  const onProductReportError = () => setCompletedCount((prev) => prev + 1);

  const hasValidContacts =
    contacts.length > 0 && contacts.every((c) => c.name && c.email);

  const lockedYears = (accountingYears || []).map(({ date }) =>
    dayjs(date).year()
  );

  const onCompanySearch = async ({ name, description }) => {
    const companyData = await fetchCompanyDataFromName(
      name,
      description,
      getAccountId()
    );
    setMatchedCompany(companyData || { name });
  };

  const onFormSubmit = async () => {
    const { relationshipId } = await fetchOurApi({
      path: "/vendors/create-customer-relationship",
      method: "POST",
      data: {
        vendorId,
        vendorName,
        ...(matchedCompany?.id ? { buyerId: matchedCompany.id } : {}),
        buyerName: matchedCompany?.name || "",
        customerContacts: contacts.map(({ id, name, email }) => ({
          id,
          name,
          email,
          explorerInvitedAt: null
        }))
      },
      callback: (response) => response
    });

    const now = new Date();

    const findCanonicalReportUrl = (year) => {
      const isFinalized = lockedYears.includes(year);
      if (isFinalized) {
        const accountingDoc = (accountingYears || []).find(
          ({ date }) => dayjs(date).year() === year
        );
        return accountingDoc?.reportUrl || null;
      }
      return (
        sessionStorage.getItem(`preliminary-report-url-${vendorId}-${year}`) ||
        null
      );
    };

    const sharedReports = selectedYears.map((year) => {
      const canonicalUrl = findCanonicalReportUrl(year);
      return {
        year,
        sharedAt: now,
        generatedAt: canonicalUrl ? now : null,
        status: "shared",
        reportUrl: canonicalUrl,
        isFinalized: lockedYears.includes(year)
      };
    });

    const yearsNeedingGeneration = selectedYears.filter(
      (year) => !findCanonicalReportUrl(year)
    );

    const sharedProducts = selectedProductIds.map((productId) => {
      const event = (productLifecycleEvents || []).find(
        (e) => e.productId === productId
      );
      const productDoc = (dbProducts || []).find((p) => p.id === productId);
      return {
        productId,
        productName: productDoc?.name || "",
        sharedAt: now,
        reportUrl: null,
        bomShared: false,
        reportShared: false,
        bomStatus: "shared",
        reportStatus: "shared",
        isFinalized: event?.bomStatus === "confirmed"
      };
    });

    await Promise.all(
      contacts.map(({ name: recipientName, email }) =>
        fetchOurApi({
          path: "/vendors/invite-customer-to-explorer",
          method: "POST",
          data: {
            vendorId,
            relationshipId,
            recipientName,
            email,
            companyName: matchedCompany?.name || "",
            senderName: vendorName
          }
        })
      )
    );

    if (matchedCompany?.id && selectedProductIds.length > 0) {
      await Promise.all(
        sharedProducts.map(({ productId, isFinalized }) =>
          fetchOurApi({
            path: "/products/share-product-with-buyer",
            method: "POST",
            data: {
              productId,
              vendorId,
              buyerId: matchedCompany.id,
              bomStatus: isFinalized ? "confirmed" : "shared"
            }
          })
        )
      );
    }

    const invitedContacts = contacts.map(({ id, name, email }) => ({
      id,
      name,
      email,
      explorerInvitedAt: now
    }));

    await updateCollectionDoc("customers", relationshipId, {
      customerContacts: invitedContacts,
      ...(sharedReports.length > 0 ? { sharedReports } : {}),
      ...(sharedProducts.length > 0 ? { sharedProducts } : {})
    });

    await mutate("customers");

    const total = yearsNeedingGeneration.length + selectedProductIds.length;

    if (total === 0) {
      onClose();
      return;
    }

    pendingRelationshipIdRef.current = relationshipId;
    pendingSharedReportsRef.current = sharedReports;
    pendingSharedProductsRef.current = sharedProducts;
    setTotalReportCount(total);
    setGeneratingYears(yearsNeedingGeneration);
    setGeneratingProductIds(selectedProductIds);
  };

  const productsWithFootprint = (dbProducts || []).filter(({ id }) => {
    const event = (productLifecycleEvents || []).find(
      (e) => e.productId === id
    );
    return !event?.bomStatus || event?.bomStatus === "confirmed";
  });

  const forms = [
    {
      name: "company",
      label: "Find Company",
      title: "Find Your Customer's Company",
      size: "lg"
    },
    {
      name: "contacts",
      label: "Add Contacts",
      title: "Add Customer Contacts",
      size: "lg"
    },
    {
      name: "reports",
      label: "Annual Reports",
      title: "Select Annual Reports to Share",
      size: "lg"
    },
    ...(productsWithFootprint.length > 0
      ? [
          {
            name: "products",
            label: "Products",
            title: "Select Products to Share",
            size: "lg"
          }
        ]
      : [])
  ];

  const rows = [
    {
      label: "Find customer's company",
      value: !!matchedCompany,
      NextStepInput: CompanySearchInput,
      nextStepInputProps: {
        onInputSelect: onCompanySearch,
        inputSelectPropName: "onSearchComplete"
      },
      form: "company"
    },
    {
      label: "Add at least one contact",
      value: hasValidContacts,
      input: <ContactsInput contacts={contacts} setContacts={setContacts} />,
      form: "contacts"
    },
    {
      label: "Select years to share",
      value: true,
      input: (
        <YearSelectionInput
          selectedYears={selectedYears}
          setSelectedYears={setSelectedYears}
          accountingYears={accountingYears}
          startDate={startDate}
        />
      ),
      form: "reports"
    },
    ...(productsWithFootprint.length > 0
      ? [
          {
            label: "Select products to share",
            value: true,
            input: (
              <ProductSelectionInput
                selectedProductIds={selectedProductIds}
                setSelectedProductIds={setSelectedProductIds}
                dbProducts={productsWithFootprint}
                productLifecycleEvents={productLifecycleEvents}
              />
            ),
            form: "products"
          }
        ]
      : [])
  ];

  const isGenerating = totalReportCount > 0;

  const generatingMergedProducts = generatingProductIds
    .map((productId) => {
      const productDoc = (dbProducts || []).find((p) => p.id === productId);
      if (!productDoc) return null;
      const event =
        (productLifecycleEvents || []).find((e) => e.productId === productId) ||
        {};
      return {
        productId,
        product: calculateMergedProductFootprint(
          productDoc,
          event,
          emissionsFactors
        ),
        isFinalized: event.bomStatus === "confirmed"
      };
    })
    .filter(Boolean);

  return (
    <Backdrop
      style={{ overflowY: "auto" }}
      boxStyle={{ width: "768px", overflowY: "auto", mb: 4 }}
      onClose={onClose}
    >
      {generatingYears.map((year) => (
        <ReportRenderer
          key={year}
          year={year}
          emissions={allEmissions}
          isFinalized={lockedYears.includes(year)}
          onUploaded={(url) => onReportUploaded(year, url)}
          onError={onReportError}
        />
      ))}
      {generatingMergedProducts.map(({ productId, product, isFinalized }) => (
        <ProductPdfCapture
          key={productId}
          product={product}
          isFinalized={isFinalized}
          onUploaded={(url) => onProductReportUploaded(productId, url)}
          onError={onProductReportError}
        />
      ))}
      {isGenerating ? (
        <ThemeProvider theme={mergeDarkTheme}>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            style={{ height: "100vh" }}
            gap={2}
          >
            <CircularProgress />
            <Typography variant="body2" color="textPrimary">
              Generating reports... ({completedCount} of {totalReportCount})
            </Typography>
          </Box>
        </ThemeProvider>
      ) : (
        <MultiPartFormLayout
          color={palette.primary.main}
          title="Add a Customer"
          subtitle="Share your emissions data with your customers"
          onFormSubmit={onFormSubmit}
          forms={forms}
          rows={rows}
          submitLoadingText="Creating customer..."
          formSubmitText="Add Customer"
          theme={mergeDarkTheme}
          buttonTheme={mainTheme}
          allowFormScrolling
        />
      )}
    </Backdrop>
  );
};

export default NewCustomerForm;
